import { motion, type Variants } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, Phone, Check, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import ImageSlideshow from "@/components/ImageSlideshow";

const defaultAdvantages = [
  "Individuelle Lösungen nach Maß",
  "Fachgerechte Fertigung und Montage",
  "Reparatur und Anpassung bestehender Bauteile",
  "Für Privatkunden und Gewerbebetriebe",
];

type ServicePageProps = {
  title: string;
  subtitle: string;
  heroImg?: string;
  heroIcon?: React.ReactNode;
  description: string[];
  details: string[];
  advantages?: string[];
  extraSection?: React.ReactNode;
  gallery?: { src: string; alt: string; caption?: string }[];
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
} satisfies Variants;

export default function ServicePage({ title, subtitle, heroImg, heroIcon, description, details, advantages, extraSection, gallery }: ServicePageProps) {
  const pageAdvantages = advantages ?? defaultAdvantages;

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-zinc-50 to-white py-16 md:py-24 border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
            <motion.div variants={fadeUp} className="mb-4">
              <Link href="/leistungen" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary transition-colors">
                <ChevronLeft className="w-4 h-4" /> Alle Leistungen
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} className="text-primary font-bold tracking-wider uppercase text-sm mb-3">
              Leistung
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-3xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight">
              {title}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-zinc-600 text-lg leading-relaxed mb-8 max-w-2xl">
              {subtitle}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Button size="lg" className="gap-2 font-semibold" asChild>
                <a href="tel:0794133514">
                  <Phone className="w-4 h-4" /> Jetzt Termin vereinbaren
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Slideshow oder Hero-Bild */}
      {(gallery?.length || heroImg || heroIcon) && (
        <section className="bg-white border-b border-zinc-100">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl py-10">
            {gallery?.length ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ImageSlideshow images={gallery} />
              </motion.div>
            ) : heroImg ? (
              <div className="rounded-xl overflow-hidden shadow-lg bg-zinc-50 border border-zinc-100">
                <img src={heroImg} alt={title} className="w-full max-h-[520px] object-contain" />
              </div>
            ) : (
              <div className="bg-zinc-50 rounded-xl h-48 flex items-center justify-center border border-zinc-100">
                {heroIcon}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Description */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              className="space-y-5 text-zinc-600 text-base leading-relaxed"
            >
              {description.map((para, i) => (
                <motion.p key={i} variants={fadeUp}>{para}</motion.p>
              ))}
              {title === "Garagentore" && (
                <motion.p variants={fadeUp}>
                  Regionale Informationen finden Sie auf unseren Seiten zu{" "}
                  <Link href="/garagentore/pfedelbach" className="font-semibold text-primary hover:underline">
                    Garagentoren in Pfedelbach
                  </Link>,{" "}
                  <Link href="/garagentore/heilbronn" className="font-semibold text-primary hover:underline">
                    Garagentoren in Heilbronn
                  </Link>,{" "}
                  <Link href="/garagentore/neuenstein" className="font-semibold text-primary hover:underline">
                    Garagentoren in Neuenstein
                  </Link>,{" "}
                  <Link href="/garagentore/kuenzelsau" className="font-semibold text-primary hover:underline">
                    Garagentoren in Künzelsau
                  </Link>,{" "}
                  <Link href="/garagentore/bretzfeld" className="font-semibold text-primary hover:underline">
                    Garagentoren in Bretzfeld
                  </Link>,{" "}
                  <Link href="/garagentore/schwaebisch-hall" className="font-semibold text-primary hover:underline">
                    Garagentoren in Schwäbisch Hall
                  </Link>,{" "}
                  <Link href="/garagentore/neuenstadt-am-kocher" className="font-semibold text-primary hover:underline">
                    Garagentore in Neuenstadt am Kocher
                  </Link>{" "}
                  und{" "}
                  <Link href="/garagentore/kupferzell" className="font-semibold text-primary hover:underline">
                    Garagentore in Kupferzell
                  </Link>.
                </motion.p>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <h2 className="text-lg font-bold text-zinc-900 mb-5">Unsere Leistungen im Detail</h2>
              <ul className="space-y-3">
                {details.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-zinc-700">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Extra Section (optional, e.g. Einbruchschutz trust block) */}
      {extraSection}

      {/* Advantages */}
      <section className="py-14 md:py-20 bg-zinc-50 border-y border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-zinc-900 mb-8">
              Warum Tür- und Torservice Britz?
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {pageAdvantages.map((adv, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-3 bg-white rounded-xl p-5 border border-zinc-100 shadow-sm">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-zinc-700 font-medium">{adv}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-zinc-900 mb-4">
              Sie benötigen Unterstützung? Wir sind für Sie da.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-zinc-600 text-lg mb-8 max-w-xl mx-auto">
              Ob Wartung, Reparatur, UVV-Prüfung oder Montage – wir beraten Sie gerne und finden die passende Lösung für Ihre Anforderungen.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="gap-2 font-semibold" asChild>
                <a href="tel:0794133514">
                  <Phone className="w-4 h-4" /> 07941 33 51 4
                </a>
              </Button>
              <Button size="lg" variant="outline" className="gap-2 border-primary text-primary hover:bg-primary/5" asChild>
                <a href="/#kontakt" onClick={() => sessionStorage.setItem("scrollTarget", "kontakt")}>
                  Jetzt Kontakt aufnehmen <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
